import { alpha } from "@mui/material/styles";

// Estilos do modal "Analisar padrão" (análise de 30 dias de um equipamento,
// aberto pela detecção de falha recorrente dos Alertas inteligentes).
// Segue o visual dos blocos da Central de Alertas: cabeçalho com título
// `mySubtitle` + caption e MetricInfo à direita, cards de indicador no mesmo
// esqueleto dos KPIs (quadrado do ícone + rótulo + valor, borda e fundo em
// alpha da cor semântica) e destaque no formato dos itens dos Alertas
// inteligentes. Cores sempre via cores.js / theme — nunca hex fixo.
const styles = (theme) => {
	const escuro = theme.palette.mode === "dark";
	const corDestaque = escuro
		? theme.palette.colors.purple.medium
		: theme.palette.colors.purple.dark;

	return {
		// Cabeçalho do Dialog no padrão linhaCabecalho dos cards.
		linhaCabecalho: {
			display: "flex",
			alignItems: "flex-start",
			justifyContent: "space-between",
			gap: 1,
		},
		titulo: {
			display: "flex",
			flexDirection: "column",
			minWidth: 0,
		},
		subtitulo: {
			color: theme.palette.text.secondary,
			fontWeight: 500,
		},
		conteudo: {
			display: "flex",
			flexDirection: "column",
			gap: 2,
		},
		// Cabeçalho das seções internas: mesma tipografia dos cards.
		cabecalhoSecao: {
			display: "flex",
			flexDirection: "column",
		},
		captionSecao: {
			color: theme.palette.text.secondary,
		},
		// Grid de colunas iguais (auto-fill, não auto-fit: a última linha
		// incompleta mantém a largura de uma coluna em vez de esticar).
		linhaIndicadores: {
			display: "grid",
			gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
			gap: 1,
		},
		// Mesmo esqueleto do card de KPI (kpiCards/styles.js).
		indicador: (cor) => ({
			minWidth: 0,
			p: 1.5,
			borderRadius: "10px",
			display: "flex",
			alignItems: "center",
			gap: 1.25,
			border: "1px solid",
			borderColor: cor || theme.palette.divider,
			backgroundColor: cor
				? alpha(cor, 0.1)
				: theme.palette.background.paper,
		}),
		quadradoIcone: (cor) => ({
			width: "36px",
			height: "36px",
			minWidth: "36px",
			borderRadius: "8px",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			backgroundColor: alpha(cor, escuro ? 0.16 : 0.1),
			"& svg": {
				fontSize: "20px",
				color: cor,
			},
		}),
		textosIndicador: {
			display: "flex",
			flexDirection: "column",
			minWidth: 0,
		},
		valorIndicador: {
			color: theme.palette.text.primary,
			fontSize: "20px",
			fontWeight: 700,
			lineHeight: 1.15,
		},
		rotuloIndicador: {
			color: theme.palette.text.secondary,
			fontSize: "13px",
			fontWeight: 600,
			lineHeight: 1.2,
		},
		// Destaque da faixa de horário, no formato dos itens dos Alertas
		// inteligentes (borda + fundo translúcidos na cor da categoria).
		observacao: {
			display: "flex",
			alignItems: "center",
			gap: 1,
			p: "10px 12px",
			borderRadius: "10px",
			border: `1px solid ${alpha(corDestaque, escuro ? 0.35 : 0.3)}`,
			backgroundColor: alpha(corDestaque, escuro ? 0.08 : 0.05),
			color: theme.palette.text.secondary,
			"& svg": {
				color: corDestaque,
				flexShrink: 0,
			},
		},
		areaGrafico: {
			mt: 0.5,
		},
		tooltipGrafico: {
			backgroundColor: theme.palette.background.paper,
			border: `1px solid ${theme.palette.divider}`,
			borderRadius: "8px",
			fontSize: "0.75rem",
		},
		// Cursor do hover do gráfico: realce sutil derivado do tema no lugar
		// do retângulo cinza chapado padrão do recharts (feio no dark).
		cursorGrafico: {
			fill: alpha(theme.palette.text.primary, 0.08),
		},
		// DataGrid com TODOS os alertas do período: altura fixa (o grid rola e
		// pagina por dentro) para o modal não crescer sem limite.
		areaTabela: {
			mt: 0.5,
			height: "320px",
			width: "100%",
		},
		// Chip compacto: as linhas do DatagridComponent têm 28px (density
		// compact) — o chip small padrão (24px) fica cortado; 20px folga.
		// Sem minWidth: o chip abraça o rótulo ("Alto" curto, "Informativo"
		// largo), em vez de esticar todos para a mesma largura.
		chipAlerta: {
			height: "20px",
			fontSize: "11px",
			"& .MuiChip-icon": {
				fontSize: "0.85rem",
			},
		},
		// Célula dos ícones dos marcos da linha do tempo (coluna única).
		atendimentoAlerta: {
			display: "flex",
			flexDirection: "row",
			alignItems: "center",
			gap: "6px",
			height: "100%",
		},
		// Ícone de marco na tabela: cor semântica do marco correspondente da
		// linha do tempo quando o registro existe; esmaecido quando não.
		iconeMarcoTabela: (cor) => ({
			fontSize: "1.05rem",
			...(cor
				? { color: cor }
				: { color: theme.palette.text.secondary, opacity: 0.5 }),
		}),
		// Fonte das linhas um pouco maior que o padrão compacto do
		// DatagridComponent (0.85rem).
		fonteTabela: {
			"& .MuiDataGrid-cell": {
				fontSize: "0.9rem",
			},
			"& .MuiDataGrid-cell .MuiTypography-caption": {
				fontSize: "0.8rem",
			},
		},
		// Ícone do tipo + nome do alerta na mesma linha; minWidth 0 para o
		// ellipsis do texto continuar funcionando dentro do flex.
		linhaTituloAlerta: {
			display: "flex",
			alignItems: "center",
			gap: "6px",
			minWidth: 0,
			width: "100%",
			height: "100%",
		},
		// Nome do alerta na tabela: corta com reticências; completo no Tooltip.
		tituloAlertaTabela: {
			display: "block",
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
			maxWidth: "100%",
		},
		estadoVazio: {
			py: 4,
			textAlign: "center",
		},
		// Mesma altura do card real de indicador (padding 12px x2 + ícone 36px).
		skeletonIndicador: {
			height: "62px",
			borderRadius: "10px",
		},
		// Título de seção (mySubtitle 18px) antes do gráfico e da tabela.
		skeletonTituloSecao: {
			width: "180px",
			height: "24px",
			mb: 1,
		},
		// Mesma altura do ResponsiveContainer do gráfico.
		skeletonGrafico: {
			height: "180px",
			borderRadius: "10px",
		},
		// Tabela: cabeçalho + linhas, somando ~a altura real de areaTabela.
		skeletonTabela: {
			display: "flex",
			flexDirection: "column",
			gap: 1,
		},
		skeletonCabecalhoTabela: {
			height: "36px",
		},
		skeletonLinha: {
			height: "34px",
			borderRadius: "8px",
		},
	};
};

export default styles;
